/**
 * レスポンスのヘッダーが届くまでに待ち時間を付けた fetch を作る（SSE 用）。
 * ヘッダーが届いた後は、ストリームの途中で切らない。待ち時間切れは通信エラーとして投げる。
 * 購読の送信は再送しない（SSELink が再試行を止めている）ので、次の送信から待ち時間が伸びる。
 * 待ち時間は WebSocket と同じ規則（8 → 16 → 32 秒）。
 */

import { type ConnectTimeoutScheduleOptions, createConnectTimeoutSchedule } from './connect-timeout-schedule';

export type ConnectTimeoutFetchOptions = ConnectTimeoutScheduleOptions & {
  fetchImpl?: typeof fetch;
  onConnectTimeout?: (info: { timeoutMs: number; consecutiveTimeouts: number }) => void;
  onResponseHeaders?: () => void;
};

export function createConnectTimeoutFetch({
  fetchImpl,
  onConnectTimeout,
  onResponseHeaders,
  ...scheduleOptions
}: ConnectTimeoutFetchOptions = {}): typeof fetch {
  const schedule = createConnectTimeoutSchedule(scheduleOptions);

  return async (input, init) => {
    const baseFetch = fetchImpl ?? fetch;
    const timeoutMs = schedule.nextTimeoutMs();
    const controller = new AbortController();
    let timedOut = false;

    // 呼び出し側（購読の解除）の中断は、ストリームの途中でも効くように、最後まで引き継ぐ
    const callerSignal = init?.signal;
    if (callerSignal?.aborted) {
      controller.abort(callerSignal.reason);
    } else {
      callerSignal?.addEventListener('abort', () => controller.abort(callerSignal.reason), { once: true });
    }

    const timer = setTimeout(() => {
      timedOut = true;
      const consecutiveTimeouts = schedule.markTimedOut();
      onConnectTimeout?.({ timeoutMs, consecutiveTimeouts });
      controller.abort();
    }, timeoutMs);

    try {
      const response = await baseFetch(input, { ...init, signal: controller.signal });
      schedule.markConnected();
      onResponseHeaders?.();
      return response;
    } catch (error) {
      throw timedOut ? new Error(`connect timeout after ${timeoutMs}ms`) : error;
    } finally {
      clearTimeout(timer);
    }
  };
}
